import {AgendaEventConsultantFields} from '@/features/agenda/components/agenda-event-form/agenda-event-consultant-fields/AgendaEventConsultantFields'
import {AgendaEventDateTimeSelector} from '@/features/agenda/components/agenda-event-form/agenda-event-date-time-selector/AgendaEventDateTimeSelector'
import {AgendaEventNotesField} from '@/features/agenda/components/agenda-event-form/agenda-event-notes-field/AgendaEventNotesField'
import {AgendaEventPatientsFields} from '@/features/agenda/components/agenda-event-form/agenda-event-patients-fields/AgendaEventPatientsFields'
import {AgendaEventRepeatScheduleFields} from '@/features/agenda/components/agenda-event-form/agenda-event-repeat-schedule-fields/AgendaRepeatScheduleFields'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {z} from 'zod'
import {useFormContext, useWatch} from 'react-hook-form'
import {AgendaEventAccessesCounters} from '@/features/agenda/components/agenda-event-accesses-counters/AgendaEventAccessesCounters'
import {AgendaEventRemoteFields} from '@/features/agenda/components/agenda-event-form/agenda-event-remote-fields/AgendaEventRemoveFields'
import {WarningBanner} from '@/components/commons/warning-banner/WarningBanner'
import {useTranslation} from '@/translations/i18n'
import {useAgendaFormDisabledOperation} from '@/features/agenda/hooks/useAgendaFormDisabledOperation'
import {AccessesToRecoveryField} from '../accesses-to-recover-field/AccessesToRecoverField'

export const AgendaEventFormSingleFieldsSchema = AgendaEventFieldsSchema.pick({
    date: true,
    patients: true,
    consultants: true,
    category: true,
    isGroupTreatment: true,
    isARecovery: true
})
type AgendaEventFormSingleFieldsSchema = z.infer<typeof AgendaEventFormSingleFieldsSchema>

export const AgendaEventFormSingleFields = ({
    defaultDate,
    mode
}: {
    isOpenedFromCalendar?: boolean
    mode?: 'create' | 'edit'
    defaultDate?: Date
}) => {
    const {t} = useTranslation()
    const {control} = useFormContext<AgendaEventFormSingleFieldsSchema>()
    const patients = useWatch({control, name: 'patients'})
    const category = useWatch({control, name: 'category'})
    const consultants = useWatch({control, name: 'consultants'})
    const isARecovery = useWatch({control, name: 'isARecovery'})
    const isGroupTreatment = useWatch({control, name: 'isGroupTreatment'})
    const isRepeatScheduleVisible =
        mode != 'edit' &&
        !isARecovery &&
        !isGroupTreatment &&
        category == 'treatments' &&
        patients?.length > 0 &&
        patients?.every(patient => !!patient.project?.expirationDate)

    const areDisabledPatientRelatedFields = useAgendaFormDisabledOperation()

    return (
        <>
            <AgendaEventPatientsFields areDisabledPatientRelatedFields={areDisabledPatientRelatedFields} mode={mode} />
            {areDisabledPatientRelatedFields ? (
                isGroupTreatment ? (
                    <WarningBanner
                        title={t('agenda:unavailable_event_operation')}
                        subtitle={t('agenda:unavailable_event_group_operation_subtitle')}
                        maxWidth="unset"
                    />
                ) : (
                    <WarningBanner
                        title={t('agenda:unavailable_event_operation')}
                        subtitle={t('agenda:unavailable_event_operation_subtitle')}
                        maxWidth="unset"
                    />
                )
            ) : (
                <>
                    <AgendaEventConsultantFields />

                    {patients?.[0]?.project && consultants?.length == 1 && consultants?.[0]?.accessType && category && (
                        <AgendaEventAccessesCounters
                            projectId={patients[0].project.id}
                            projectStartDate={patients[0].project.startDate}
                            projectExpirationDate={patients[0].project.expirationDate}
                            accessTypeId={consultants[0].accessType.id}
                            accessType={consultants[0].accessType.accessType}
                            category={category}
                        />
                    )}

                    {patients?.[0]?.project &&
                        consultants?.length == 1 &&
                        consultants?.[0]?.accessType &&
                        category &&
                        isARecovery && (
                            <AccessesToRecoveryField
                                patientId={patients[0].id}
                                accessType={consultants[0].accessType.accessType}
                            />
                        )}

                    <AgendaEventDateTimeSelector index={0} mode={mode} defaultDate={defaultDate} />

                    {isRepeatScheduleVisible && <AgendaEventRepeatScheduleFields />}

                    <AgendaEventNotesField />
                    <AgendaEventRemoteFields />
                </>
            )}
        </>
    )
}
